import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { CircleNotchIcon, PlusIcon } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

export interface CharacterRef {
    id: string;
    name: string;
    snapshotUrl: string;  // Reference sheet URL for generation (camelCase to match imageGeneration.ts)
    snapshotUrls?: string[];  // Multiple reference images for better identity consistency
    metadata: any;  // Character metadata (text description, usage prompt, etc.)
}

interface CharacterSelectorProps {
    onSelect: (char: CharacterRef | null) => void;
    selectedId: string | null;
}

export const CharacterSelector: React.FC<CharacterSelectorProps> = ({ onSelect, selectedId }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [characters, setCharacters] = useState<CharacterRef[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) return;

        const fetchCharacters = async () => {
            const { data, error } = await supabase
                .from('characters')
                .select('id, name, snapshot_url, dna_metadata')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false });

            if (!error && data) {
                // Transform legacy snake_case to camelCase
                const transformed = data.map((char: any) => ({
                    id: char.id,
                    name: char.name,
                    snapshotUrl: char.snapshot_url,
                    metadata: char.dna_metadata
                }));
                setCharacters(transformed);
            }
            setLoading(false);
        };

        fetchCharacters();
    }, [user]);

    if (loading) return <div className="h-24 flex items-center justify-center text-zinc-500"><CircleNotchIcon className="w-4 h-4 animate-spin" /></div>;
    if (characters.length === 0) return null;

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Character Reference</label>
                <button onClick={() => navigate('/characters')} className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
                    <PlusIcon className="w-3 h-3" /> New
                </button>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                <button
                    onClick={() => onSelect(null)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg border-2 flex items-center justify-center transition-all ${selectedId === null
                        ? 'border-indigo-500 bg-indigo-500/10 text-white'
                        : 'border-zinc-800 bg-zinc-900 text-zinc-500 hover:border-zinc-700'
                        }`}
                >
                    <span className="text-xs font-bold">None</span>
                </button>

                {characters.map((char) => (
                    <button
                        key={char.id}
                        onClick={() => onSelect(selectedId === char.id ? null : char)}
                        className={`relative flex-shrink-0 w-16 h-16 rounded-lg border-2 overflow-hidden transition-all group ${selectedId === char.id
                            ? 'border-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.5)]'
                            : 'border-zinc-800 hover:border-zinc-600'
                            }`}
                        title={char.name}
                    >
                        <img src={char.snapshotUrl} alt={char.name} className="w-full h-full object-cover" />
                        <div className={`absolute inset-0 bg-carbon/40 flex items-end justify-center p-1 ${selectedId === char.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                            <span className="text-[9px] font-bold text-white truncate max-w-full">{char.name}</span>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
};
